// Updated AdditionalInfoForm.js with improved subscription and payment handling

import React, { useState, useEffect } from "react";
import axios from "axios";
import API_BASE_URL from "./../../common/config";
import API_URL_dash from "../../common/config2";
import {
  SuccessNotification,
  ErrorNotification,
} from "../../tosterNotification/tosters";
import { focusOnFirstError } from "./focusUtils";
import ReactGA from "react-ga4";

const AdditionalInfoForm = ({
  formValues,
  setFormValues,
  formErrors,
  setFormErrors,
  setIsLoading,
  navigate,
  resumeFile,
  setResumeFile,
  inputRefs,
  careerLaunch,
  subscriptionData, // Subscription data from pricing page
}) => {
  const [isResume, setIsResume] = useState(false);
  const [fileName, setFileName] = useState("");
  const [submitButtonText, setSubmitButtonText] = useState("Submit");
  const [submitAttempts, setSubmitAttempts] = useState(0);

  useEffect(() => {
    if (formValues.fileLocation) {
      setIsResume(true);
      setFileName(resumeFile?.name || "");
    }
  }, [formValues.fileLocation, resumeFile]);

  // Update button text based on subscription plan
  useEffect(() => {
    if (subscriptionData && subscriptionData.plan !== "FREE MVP TRIAL") {
      setSubmitButtonText("Proceed To Payment");
    } else {
      setSubmitButtonText("Submit");
    }
  }, [subscriptionData]);

  const validateAllFields = () => {
    const errors = {};

    // PersonalInfoForm mandatory fields
    if (!formValues.firstname) errors.firstname = "First name is required";
    if (!formValues.lastname) errors.lastname = "Last name is required";
    if (!formValues.age) errors.age = "Age group is required";
    if (!formValues.country) errors.country = "Country is required";

    // Country validation
    if (formValues.country) {
      if (formValues.country !== "IN" && formValues.country !== "US") {
        errors.country = "CLIF AI is only operating in India and USA for now";
      }
    }

    // EducationForm mandatory fields
    if (!formValues.education) errors.education = "Education level is required";
    if (!formValues.specialization)
      errors.specialization = "Specialization is required";

    if (careerLaunch === false) {
      if (!formValues.gender) errors.gender = "Experience is required";
      // EmploymentForm mandatory fields
      if (!formValues.current_employer)
        errors.current_employer = "Current employer is required";
      if (!formValues.currentRole)
        errors.currentRole = "Current role is required";
    }

    // Job goals validation
    if (!formValues.clif_check) {
      if (!formValues.job_goal) {
        errors.job_goal = "Dream job goal is required";
      } else if (formValues.job_goal.trim().length < 50) {
        errors.job_goal =
          "Dream Job Goal should be at least 50 characters long";
      } else if (formValues.job_goal.trim().length > 250) {
        errors.job_goal =
          "Dream Job Goal should be at most 250 characters long";
      }
    }

    // Resume validation
    if (!formValues.fileUpload) {
      errors.fileUpload = "Resume is required";
    }

    // Employment goals
    if (!formValues.desired_employer)
      errors.desired_employer = "Desired employer is required";
    if (!formValues.desiredRole)
      errors.desiredRole = "Desired role is required";
    if (!formValues.desired_country)
      errors.desired_country = "Desired country is required";

    return errors;
  };

  const handleChange = (e) => {
    const { name, value, files } = e.target;
    const newValue = files && files[0] ? files[0] : value;

    setFormValues((prev) => ({ ...prev, [name]: newValue }));
    if (files && files[0]) {
      setResumeFile(files[0]);
      setIsResume(true);
      setFileName(files[0].name);
    }

    // Clear error for this field when changed
    setFormErrors((prev) => ({
      ...prev,
      [name]: "",
    }));
  };

  // Enhanced submit function with better error handling and recovery
  const handleSubmit = async (e) => {
    if (e) e.preventDefault();

    // Track submission attempt
    setSubmitAttempts((prev) => prev + 1);

    // Track event
    ReactGA.event({
      category: "User",
      action: "Clicked Button",
      label: "Sign Up",
    });

    // Validate all fields before submission
    const localErrors = validateAllFields();
    setFormErrors(localErrors);

    if (Object.keys(localErrors).length > 0) {
      focusOnFirstError(localErrors, inputRefs);
      return;
    }

    try {
      setIsLoading(true);
      const formData = new FormData();

      // Add all form values to formData
      for (const key in formValues) {
        if (key === "fileUpload" && !formValues[key] && resumeFile) {
          formData.append(key, resumeFile);
        } else if (key !== "password" && key !== "cpassword") {
          // Don't log sensitive fields in console logs
          console.log(`Adding field ${key} with value: ${formValues[key]}`);
          formData.append(key, formValues[key]);
        } else {
          // Still add password fields to form data, just don't log them
          formData.append(key, formValues[key]);
        }
      }

      // Add subscription data if it exists
      if (subscriptionData) {
        formData.append("subscriptionPlan", subscriptionData.plan);
        formData.append("subscriptionPrice", subscriptionData.price);
        formData.append("subscriptionDuration", subscriptionData.duration);
        if (subscriptionData.priceId) {
          formData.append("stripePriceId", subscriptionData.priceId);
        }

        // Log subscription details (excluding price ID which is sensitive)
        console.log("Adding subscription data:", {
          plan: subscriptionData.plan,
          price: subscriptionData.price,
          duration: subscriptionData.duration,
        });
      }

      // Implement better error handling with timeout
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 30000); // 30-second timeout

      const response = await axios.post(
        `${API_BASE_URL}/auth/signup`,
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" },
          signal: controller.signal,
        }
      );

      clearTimeout(timeoutId);

      if (response.data.success) {
        SuccessNotification("Signup Successful!", 4000);
        if (response.data && response.data.data) {
          // Store user data and auth tokens
          localStorage.setItem("user", JSON.stringify(response.data.data));

          // Store tokens if they exist
          if (response.data.token) {
            localStorage.setItem("token", JSON.stringify(response.data.token));
          }

          if (response.data.refreshToken) {
            localStorage.setItem(
              "refreshToken",
              JSON.stringify(response.data.refreshToken)
            );
          }

          localStorage.setItem("sUserid", response.data.data._id);

          // Handle paid subscription redirects
          if (
            subscriptionData &&
            subscriptionData.plan !== "FREE MVP TRIAL" &&
            response.data.data
          ) {
            // Track the event
            ReactGA.event({
              category: "Subscription",
              action: "Redirecting to Payment",
              label: subscriptionData.plan,
            });

            // Prepare payment data
            const paymentData = {
              userId: response.data.data._id,
              planName: subscriptionData.plan,
              planPrice: subscriptionData.price,
              planDuration: subscriptionData.duration,
              stripePriceId: subscriptionData.priceId,
              userEmail: formValues.email,
              userName: `${formValues.firstname} ${formValues.lastname}`,
            };

            // Store data for payment recovery if needed
            localStorage.setItem("pendingPayment", JSON.stringify(paymentData));

            // Redirect to payment page
            navigate("/payment", { state: paymentData });
            return;
          }

          // For free plans, continue with normal user flow
          // If subscription data exists, add it to the user object
          if (subscriptionData && response.data.data) {
            const userData = {
              ...response.data.data,
              subscription: {
                plan: subscriptionData.plan,
                price: subscriptionData.price,
                duration: subscriptionData.duration,
              },
            };
            localStorage.setItem("user", JSON.stringify(userData));
          }

          // Get user data and encoded tokens
          const userData = encodeURIComponent(
            JSON.stringify(response.data.data)
          );
          const userId = response.data.data._id;
          const token = response.data.token
            ? encodeURIComponent(JSON.stringify(response.data.token))
            : "";
          const refreshToken = response.data.refreshToken
            ? encodeURIComponent(JSON.stringify(response.data.refreshToken))
            : "";

          // Redirect with all necessary auth data
          window.location.href = `${API_URL_dash}/dashboard?userData=${userData}&userId=${userId}&token=${token}&refreshToken=${refreshToken}`;
        }
      } else {
        // Handle explicit failure response
        ErrorNotification(
          response.data.message || "Registration failed. Please try again."
        );
      }
    } catch (error) {
      console.error("Registration error:", error);
      handleRegistrationFailure(error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleRegistrationFailure = (error) => {
    setIsLoading(false);

    // Customize error message based on error type
    let errorMessage = "Registration couldn't be completed.";
    let errorTitle = "Registration Failed";
    let retryOption = true;

    if (error.code === "ECONNABORTED" || error.message.includes("timeout")) {
      errorMessage =
        "The server took too long to respond. Please try again when you have a better internet connection.";
      errorTitle = "Request Timeout";
    } else if (error.response) {
      // Server responded with an error status
      const status = error.response.status;

      if (status === 409) {
        errorMessage =
          "This email is already registered. Please log in instead.";
        errorTitle = "Account Already Exists";
        retryOption = false;
      } else if (status === 413) {
        errorMessage =
          "Your resume file is too large. Please upload a smaller file (max 5MB).";
        errorTitle = "File Too Large";
      } else if (status >= 500) {
        errorMessage =
          "Our servers are currently experiencing issues. Please try again in a few minutes.";
        errorTitle = "Server Error";
      } else {
        // Use server-provided message if available
        errorMessage = error.response.data?.message || errorMessage;
      }
    } else if (!error.response && !navigator.onLine) {
      errorMessage =
        "You appear to be offline. Please check your internet connection and try again.";
      errorTitle = "Network Error";
    }

    // Show comprehensive error message with recovery options
    const errorComponent = (
      <div>
        <h6>{errorTitle}</h6>
        <p>{errorMessage}</p>
        {retryOption && (
          <p className="mt-2">
            <strong>Possible solutions:</strong>
            <ul className="mt-1 mb-0">
              <li>Check your internet connection</li>
              <li>Reduce resume file size if it's large</li>
              <li>Try again in a few minutes</li>
            </ul>
          </p>
        )}
      </div>
    );

    // Use custom notification with retry button if retrying is an option
    ErrorNotification(
      errorComponent,
      10000,
      retryOption,
      retryOption ? () => handleSubmit() : null
    );

    // Log detailed error for support
    console.error("Registration failure details:", {
      error: error.message,
      status: error.response?.status,
      response: error.response?.data,
      subscriptionPlan: subscriptionData?.plan,
      submitAttempts: submitAttempts + 1,
    });
  };

  return (
    <form onSubmit={handleSubmit} className="axil-contact-form">
      {/* Display subscription plan info if available */}
      {subscriptionData && (
        <div
          className={`subscription-confirmation mb-3 p-3 border rounded ${
            subscriptionData.plan === "FREE MVP TRIAL"
              ? "bg-light"
              : "bg-light-purple"
          }`}
        >
          <h6 className="mb-2">
            Subscription Plan:{" "}
            <span className="plan-name">{subscriptionData.plan}</span>
          </h6>
          <div className="d-flex justify-content-between mb-1 subscription-details">
            <div>
              <p className="mb-1">
                Price:{" "}
                <span className="price-value">{subscriptionData.price}</span>
              </p>
              <p className="mb-1">
                Duration:{" "}
                <span className="duration-value">
                  {subscriptionData.duration}
                </span>
              </p>
            </div>
            {subscriptionData.plan !== "FREE MVP TRIAL" && (
              <div className="payment-badge">Payment Required</div>
            )}
          </div>

          {subscriptionData.plan !== "FREE MVP TRIAL" && (
            <p className="mb-0 mt-2 text-note">
              <strong>Note:</strong> You'll be redirected to payment after
              registration.
            </p>
          )}
        </div>
      )}

      <div className="">
        <button
          name="submit-btn"
          type="submit"
          className="btn-primary s_button d-flex justify-content-end"
          style={{ marginLeft: "auto", textTransform: "capitalize" }}
          onMouseOver={(e) => {
            e.target.style.background = "#006600";
            e.target.style.boxShadow = "0 3px 6px rgba(108, 92, 231, 0.4)";
          }}
          onMouseOut={(e) => {
            e.target.style.background = "#6600CC";
            e.target.style.boxShadow = "0 2px 4px rgba(108, 92, 231, 0.3)";
          }}
        >
          {submitButtonText}
        </button>
      </div>

      <style jsx>{`
        .subscription-confirmation {
          background-color: #f8f9fa;
          border-color: #6600cc !important;
          border-left-width: 4px !important;
        }

        .bg-light-purple {
          background-color: rgba(102, 0, 204, 0.05);
        }

        .subscription-confirmation h6 {
          color: #6600cc;
          font-weight: 600;
        }

        .plan-name,
        .price-value,
        .duration-value {
          font-weight: 600;
          color: #6600cc;
        }

        .payment-badge {
          background-color: rgba(102, 0, 204, 0.1);
          color: #6600cc;
          padding: 4px 8px;
          border-radius: 12px;
          font-size: 12px;
          font-weight: 600;
          height: fit-content;
        }

        .text-note {
          font-size: 13px;
          color: #555;
        }

        .subscription-details {
          align-items: flex-start;
        }
      `}</style>
    </form>
  );
};

export default AdditionalInfoForm;
